import { Box, FormLabel, Input, InputAdornment, Slider, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ReactSelect from 'components/common/react-select/ReactSelect';
import ColorField, { themeTokenToCssVar } from 'app/app/editor/tabs/settings/style-editor/ColorField';
import FontField from 'app/app/editor/tabs/settings/style-editor/FontField';
import SizeField from 'app/app/editor/tabs/settings/style-editor/SizeField';
import styles from './StyleEditorContainer.styles';
import FieldBySide from 'app/app/editor/tabs/settings/style-editor/FieldBySide';

import { IComponentNode } from 'app/app/editor/index.functions';
import React, { useEffect, useState } from 'react';
import IconMi from 'components/common/icon/IconMi';
import SearchSlider from 'app/search/global/SearchSlider';
import UploadImage from 'components/common/upload-control/UploadImage';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
interface StylesEditorContainerProps {
    formState: any;
    setFormState: (value: any) => void;
    onChangeStyle: (value: any) => void;
    uid: string;
    node: IComponentNode;
}

function BackgroundImageUpload({
    onImageChange,
    currentImage,
}: {
    onImageChange: (dataUrl: string) => void;
    currentImage: string;
}) {
    const previewSrc = currentImage?.replace(/^url\(['"]?(.*?)['"]?\)$/, '$1') || '';
    const subForm = useCustomSimplifiedForm({ bg_image: previewSrc, bg_image_state: '' });
    const watch = subForm.form.hookFormWatch();

    useEffect(() => {
        const bgImage = watch.bg_image;
        if (typeof bgImage === 'string' && bgImage !== previewSrc) {
            onImageChange(bgImage);
        } else if (Array.isArray(bgImage) && bgImage.length === 0 && previewSrc !== '') {
            onImageChange('');
        }
    }, [watch.bg_image]);

    return (
        <UploadImage
            controlProps={{
                form: subForm.form,
                name: 'bg_image',
                onChange: (eventOrValue: any) => {
                    const file = eventOrValue?.target?.files?.[0];
                    if (file) {
                        const reader = new FileReader();
                        reader.onload = (evt) => {
                            subForm.form.hookFormSetValue('bg_image', evt.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                    } else {
                        const files = eventOrValue?.target?.files;
                        subForm.form.hookFormSetValue('bg_image', files !== undefined ? files : eventOrValue);
                    }
                },
                value: watch.bg_image,
                label: 'Upload',
                removeActionText: '',
                removeActionView: 'button',
                removeButtonColor: 'neutral',
                accept: 'image/*',
                previewsList: previewSrc ? [previewSrc] : [],
                showRemoveAction: true,
            }}
        />
    );
}

const defaultPropertyList: any = {
    'flex-direction': {
        default: 'column',
    },
    'flex-wrap': {
        default: 'wrap',
    },
    gap: {
        default: '0px',
    },
    'justify-content': {
        default: 'flex-start',
    },
    'align-content': {
        default: 'flex-start',
    },
    'align-items': {
        default: 'flex-start',
    },
    'justify-self': {
        default: '',
    },
    'align-self': {
        default: '',
    },
    width: {
        default: '',
    },
    height: {
        default: '',
    },
    margin: {
        default: '',
    },
    padding: {
        default: '',
    },
    'background-color': {
        default: '',
    },
    'background-image': {
        default: '',
    },
    'background-repeat': {
        default: '',
    },
    'background-type': {
        default: 'color',
    },
    'border-color': {
        default: '',
    },
    'border-radius': {
        default: '',
    },
    'border-width': {
        default: '',
    },
    resizable: {
        default: 'off',
    },
    'font-size': {
        default: '12px',
    },
    'font-family': {
        default: '',
    },
    color: {
        default: '',
    },
    'size-type-width': {
        default: 'fill',
    },
    'size-type-height': {
        default: 'fill',
    },
    'background-size': {
        default: 'auto',
    },
};

export const getAppBuilderCurrentStyleValue = (formState: any, propertyName: string) => {
    const val = formState[propertyName];
    if (undefined === val) return defaultPropertyList?.[propertyName]?.default;
    if (val && typeof val === 'object') {
        if (val.source === 'theme') return themeTokenToCssVar[val.value] ?? '';
        return val.value ?? '';
    }
    return val;
};

const StyleSection = ({
    alias,
    collapsedSection,
    toggleCollapseState,
    title,
    children,
}: {
    alias: string;
    collapsedSection: any;
    toggleCollapseState: (alias: string) => void;
    title: string;
    children: any;
}) => {
    return (
        <Stack sx={styles.section} className={collapsedSection?.[alias] ? 'section--collapsed' : 'section--expanded'}>
            <Stack
                direction={'row'}
                sx={styles.sectionTitle}
                onClick={() => {
                    toggleCollapseState(alias);
                }}
            >
                <Box sx={styles.sectionTitleLabel}>{title}</Box>
                <Box sx={styles.sectionTitleIcon}>
                    <IconMi icon={collapsedSection?.[alias] ? 'chevron-down' : 'chevron-up'} />
                </Box>
            </Stack>
            <Stack sx={styles.sectionControlsContainer} spacing={1}>
                {children}
            </Stack>
        </Stack>
    );
};
export default function StylesEditorContainer({
    formState,
    setFormState = () => {},
    onChangeStyle = () => {},
    uid,
    node,
}: StylesEditorContainerProps) {
    const isMenuContainer = node.subtype == 'page_menu';
    const containerDefaults = node.type === 'container' ? { text: false } : {};
    const visibleSections = { ...containerDefaults, ...(node.params.layout?.settings?.visibleStyleSection ?? {}) };
    const isSectionVisible = (section: string) => visibleSections[section] !== false;
    const isSectionFieldVisible = (section: string, field: string) => {
        const sectionValue = visibleSections[section];
        if (sectionValue === undefined) return true;
        if (typeof sectionValue !== 'object' || sectionValue === null) return sectionValue !== false;
        return sectionValue[field] !== false;
    };
    const isLayoutFieldVisible = (field: string) => isSectionFieldVisible('layout', field);

    const getInitialBackgroundType = (): 'color' | 'url' | 'upload' => {
        // Read raw value — if explicitly stored (including 'color'), trust it over background-image
        const storedType = formState['background-type'];
        if (storedType) return storedType as 'color' | 'url' | 'upload';
        // No explicit type yet — derive from background-image (backward compat)
        const bgImage = getAppBuilderCurrentStyleValue(formState, 'background-image');
        if (bgImage?.includes('url(')) return bgImage.includes('url(data:') ? 'upload' : 'url';
        return 'color';
    };
    const [backgroundType, setBackgroundType] = useState<'color' | 'url' | 'upload'>(getInitialBackgroundType);

    useEffect(() => {
        setBackgroundType(getInitialBackgroundType());
    }, [formState['background-type'], formState['background-image']]);

    const [collapsedSection, setCollapsedSection] = useState<{ [key: string]: boolean }>({
        spacing: true,
        border: true,
        background: true,
        text: true,
    });
    const isWrapping =
        getAppBuilderCurrentStyleValue(formState, 'flex-direction') === 'column' ||
        getAppBuilderCurrentStyleValue(formState, 'flex-wrap') === 'wrap';
    const alignProperty = isWrapping ? 'align-content' : 'align-items';
    const alignPropertyOpposite = isWrapping ? 'align-items' : 'align-content';

    const getTextField = (propertyName: string, placeholderValue?: string, endAdornment?: any) => {
        const tmpValue = getAppBuilderCurrentStyleValue(formState, propertyName);
        let value = tmpValue;

        switch (propertyName) {
            case 'border-width':
                value = tmpValue.replace('px', '');
                break;
            case 'background-image':
                value = (tmpValue.match(/url\(['"]?(.*?)['"]?\)/) || [])[1] ?? '';
                break;
        }

        return (
            <Input
                className={'design-variant--secondary'}
                fullWidth
                value={value}
                onChange={(e) => {
                    if (propertyName === 'border-width') {
                        onChangeStyle({
                            [propertyName]: isNaN(+e.target.value) ? e.target.value : `${e.target.value}px`,
                        });
                    } else if (propertyName === 'background-image') {
                        onChangeStyle({
                            'background-image': e.target.value ? `url(${e.target.value})` : '',
                            'background-repeat': 'no-repeat',
                        });
                    } else {
                        onChangeStyle({ [propertyName]: e.target.value });
                    }
                }}
                placeholder={placeholderValue ?? ''}
                inputProps={{ 'data-1p-ignore': true }}
                endAdornment={endAdornment}
            />
        );
    };

    const toggleCollapseState = (sectionName: string) => {
        setCollapsedSection({ ...collapsedSection, [sectionName]: !collapsedSection?.[sectionName] });
    };

    return (
        <Stack>
            {isSectionVisible('size') && (
                <StyleSection
                    alias={'size'}
                    title={'Size'}
                    collapsedSection={collapsedSection}
                    toggleCollapseState={toggleCollapseState}
                >
                    {isSectionFieldVisible('size', 'width') && (
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Width
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <SizeField
                                    formState={formState}
                                    onChangeStyle={onChangeStyle}
                                    propertyName={'width'}
                                    uid={uid}
                                />
                            </Box>
                        </Box>
                    )}
                    {!isMenuContainer && isSectionFieldVisible('size', 'height') && (
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Height
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <SizeField
                                    formState={formState}
                                    onChangeStyle={onChangeStyle}
                                    propertyName={'height'}
                                    uid={uid}
                                />
                            </Box>
                        </Box>
                    )}
                </StyleSection>
            )}

            {isSectionVisible('layout') && (
                <StyleSection
                    alias={'layout'}
                    title={'Layout'}
                    collapsedSection={collapsedSection}
                    toggleCollapseState={toggleCollapseState}
                >
                    {isMenuContainer ? (
                        <>
                            {isLayoutFieldVisible('dockable') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Dockable
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <ToggleButtonGroup
                                            fullWidth
                                            className={'design-variant--secondary'}
                                            value={
                                                getAppBuilderCurrentStyleValue(formState, 'left') == 'auto'
                                                    ? 'right'
                                                    : 'left'
                                            }
                                            exclusive
                                            onChange={(event, value) => {
                                                if (value !== null) {
                                                    onChangeStyle({
                                                        left: value == 'left' ? 0 : 'auto',
                                                        right: value == 'left' ? 'auto' : 0,
                                                    });
                                                }
                                            }}
                                        >
                                            <ToggleButton value="left">Left</ToggleButton>
                                            <ToggleButton value="right">Right</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                </Box>
                            )}

                            {isLayoutFieldVisible('collapse-with') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Collapse with
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <ToggleButtonGroup
                                            fullWidth
                                            className={'design-variant--secondary'}
                                            value={
                                                getAppBuilderCurrentStyleValue(formState, 'menu-collapse-type') ?? 'x'
                                            }
                                            exclusive
                                            onChange={(event, value) => {
                                                if (value !== null) {
                                                    onChangeStyle({ 'menu-collapse-type': value });
                                                }
                                            }}
                                        >
                                            <ToggleButton value="x">
                                                <IconMi icon={'times'} />
                                            </ToggleButton>
                                            <ToggleButton value="arrow">
                                                <IconMi icon={'chevrons-left'} />
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Stack spacing={1}>
                            {isLayoutFieldVisible('direction') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Direction
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <ToggleButtonGroup
                                            value={getAppBuilderCurrentStyleValue(formState, 'flex-direction')}
                                            exclusive
                                            onChange={(event, value) => {
                                                if (value !== null) {
                                                    if (value === 'column') {
                                                        const currentAlign = getAppBuilderCurrentStyleValue(
                                                            formState,
                                                            alignProperty,
                                                        );
                                                        onChangeStyle({
                                                            'flex-direction': 'column',
                                                            'flex-wrap': 'wrap',
                                                            'align-content': currentAlign,
                                                            'align-items': currentAlign,
                                                        });
                                                    } else {
                                                        onChangeStyle({ 'flex-direction': value });
                                                    }
                                                }
                                            }}
                                            sx={{
                                                svg: { width: '16px', height: '16px' },
                                            }}
                                            fullWidth
                                            className={'design-variant--secondary'}
                                        >
                                            <ToggleButton value="row">
                                                <IconMi icon={'arrows-alt-h'} />
                                                <Box sx={{ ml: 1 }}>Horizontal</Box>
                                            </ToggleButton>
                                            <ToggleButton value="column">
                                                <IconMi icon={'arrows-alt-v'} />
                                                <Box sx={{ ml: 1 }}>Vertical</Box>
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                </Box>
                            )}
                            {isLayoutFieldVisible('wrap') &&
                                getAppBuilderCurrentStyleValue(formState, 'flex-direction') === 'row' && (
                                    <Box sx={styles.propertyContainer}>
                                        <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                            Wrap
                                        </FormLabel>
                                        <Box sx={styles.propertyControlHolder}>
                                            <ToggleButtonGroup
                                                value={getAppBuilderCurrentStyleValue(formState, 'flex-wrap')}
                                                exclusive
                                                onChange={(event, value) => {
                                                    if (value !== null) {
                                                        const currentAlign = getAppBuilderCurrentStyleValue(
                                                            formState,
                                                            alignProperty,
                                                        );
                                                        if (value === 'wrap') {
                                                            onChangeStyle({
                                                                'flex-wrap': 'wrap',
                                                                'align-content': currentAlign,
                                                                'align-items': currentAlign,
                                                            });
                                                        } else {
                                                            onChangeStyle({
                                                                'flex-wrap': 'nowrap',
                                                                'align-items': currentAlign,
                                                                'align-content': '',
                                                            });
                                                        }
                                                    }
                                                }}
                                                fullWidth
                                                className={'design-variant--secondary'}
                                            >
                                                <ToggleButton value="wrap">Yes</ToggleButton>
                                                <ToggleButton value="nowrap">No</ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>
                                    </Box>
                                )}
                        </Stack>
                    )}
                    {!isMenuContainer && (
                        <>
                            {isLayoutFieldVisible('align') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Align
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <ToggleButtonGroup
                                            value={getAppBuilderCurrentStyleValue(formState, alignProperty)}
                                            exclusive
                                            onChange={(_, value) => {
                                                if (value !== null) {
                                                    if (isWrapping) {
                                                        onChangeStyle({ 'align-content': value, 'align-items': value });
                                                    } else {
                                                        onChangeStyle({
                                                            [alignProperty]: value,
                                                            [alignPropertyOpposite]: '',
                                                        });
                                                    }
                                                }
                                            }}
                                            fullWidth
                                            className={'design-variant--secondary'}
                                        >
                                            {getAppBuilderCurrentStyleValue(formState, 'flex-direction') ===
                                            'column' ? (
                                                <>
                                                    <ToggleButton value="flex-start">
                                                        <IconMi icon={'builder-alignment-left'} fontSize={'16'} />
                                                        <Box sx={{ ml: 1 }}>Left</Box>
                                                    </ToggleButton>
                                                    <ToggleButton value="center">
                                                        <IconMi icon={'builder-alignment-center'} fontSize={'16'} />
                                                        <Box sx={{ ml: 1 }}>Center</Box>
                                                    </ToggleButton>
                                                    <ToggleButton value="flex-end">
                                                        <IconMi icon={'builder-alignment-right'} fontSize={'16'} />
                                                        <Box sx={{ ml: 1 }}>Right</Box>
                                                    </ToggleButton>
                                                </>
                                            ) : (
                                                <>
                                                    <ToggleButton value="flex-start">
                                                        <IconMi
                                                            icon={'builder-alignment-left'}
                                                            fontSize={'16'}
                                                            sx={{ transform: 'rotate(90deg)' }}
                                                        />
                                                        <Box sx={{ ml: 1 }}>Top</Box>
                                                    </ToggleButton>
                                                    <ToggleButton value="center">
                                                        <IconMi
                                                            icon={'builder-alignment-center'}
                                                            fontSize={'16'}
                                                            sx={{ transform: 'rotate(90deg)' }}
                                                        />
                                                        <Box sx={{ ml: 1 }}>Center</Box>
                                                    </ToggleButton>
                                                    <ToggleButton value="flex-end">
                                                        <IconMi
                                                            icon={'builder-alignment-right'}
                                                            fontSize={'16'}
                                                            sx={{ transform: 'rotate(90deg)' }}
                                                        />
                                                        <Box sx={{ ml: 1 }}>Bottom</Box>
                                                    </ToggleButton>
                                                </>
                                            )}
                                        </ToggleButtonGroup>
                                    </Box>
                                </Box>
                            )}
                            {isLayoutFieldVisible('distribute') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Distribute
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <ReactSelect
                                            className={'design-variant--secondary'}
                                            data={[
                                                { label: 'Start', value: 'flex-start' },
                                                { label: 'Center', value: 'center' },
                                                { label: 'End', value: 'flex-end' },
                                                { label: 'Space Between', value: 'space-between' },
                                                { label: 'Space Around', value: 'space-around' },
                                            ]}
                                            value={[getAppBuilderCurrentStyleValue(formState, 'justify-content')]}
                                            update={(value: string) => {
                                                onChangeStyle({ 'justify-content': value });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                            {isLayoutFieldVisible('gap') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Gap
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                            <Input
                                                className={'design-variant--secondary'}
                                                value={parseInt(getAppBuilderCurrentStyleValue(formState, 'gap')) || 0}
                                                onChange={(e) => {
                                                    const num = e.target.value;
                                                    onChangeStyle({ gap: `${num}px` });
                                                }}
                                                sx={{ width: '80px', flexShrink: 0 }}
                                                endAdornment={
                                                    <InputAdornment position="end" sx={{ mx: 0 }}>
                                                        <Box sx={{ px: 1 }}>px</Box>
                                                    </InputAdornment>
                                                }
                                                inputProps={{ 'data-1p-ignore': true }}
                                            />
                                            <SearchSlider
                                                value={parseInt(getAppBuilderCurrentStyleValue(formState, 'gap')) || 0}
                                                min={0}
                                                max={100}
                                                onChange={(_, value) => {
                                                    onChangeStyle({ gap: `${value}px` });
                                                }}
                                            />
                                        </Stack>
                                    </Box>
                                </Box>
                            )}
                            {isLayoutFieldVisible('padding') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Padding
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <FieldBySide
                                            type={'side'}
                                            propertyName={'padding'}
                                            formState={formState}
                                            onChangeStyle={onChangeStyle}
                                            uid={uid}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </StyleSection>
            )}

            {isSectionVisible('border') && (
                <StyleSection
                    alias={'border'}
                    title={'Border'}
                    collapsedSection={collapsedSection}
                    toggleCollapseState={toggleCollapseState}
                >
                    {(isSectionFieldVisible('border', 'width') || isSectionFieldVisible('border', 'radius')) && (
                        <Stack direction="row" spacing={1} sx={styles.propertyContainer}>
                            {isSectionFieldVisible('border', 'width') && (
                                <Box>
                                    {' '}
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Border Width
                                    </FormLabel>
                                    <Stack direction={'row'} spacing={1}>
                                        <Box sx={{ ...styles.propertyControlHolder, width: '100%' }}>
                                            {getTextField(
                                                'border-width',
                                                '0',
                                                <InputAdornment position="end" sx={{ mx: 0 }}>
                                                    <Box sx={{ px: 1 }}>px</Box>
                                                </InputAdornment>,
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            )}
                            {isSectionFieldVisible('border', 'radius') && (
                                <Box>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Border Radius
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <FieldBySide
                                            type={'corner'}
                                            propertyName={'border-radius'}
                                            formState={formState}
                                            onChangeStyle={onChangeStyle}
                                            uid={uid}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    )}
                    {isSectionFieldVisible('border', 'color') && (
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Color
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ColorField
                                    propertyName={'border-color'}
                                    onChangeStyle={onChangeStyle}
                                    formState={formState}
                                    uid={uid}
                                />
                            </Box>
                        </Box>
                    )}
                </StyleSection>
            )}

            {isSectionVisible('background') && (
                <StyleSection
                    alias={'background'}
                    title={'Background'}
                    collapsedSection={collapsedSection}
                    toggleCollapseState={toggleCollapseState}
                >
                    {isSectionFieldVisible('background', 'type') && (
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Type
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ToggleButtonGroup
                                    fullWidth
                                    className={'design-variant--secondary'}
                                    value={backgroundType}
                                    exclusive
                                    onChange={(_, value) => {
                                        if (value !== null) {
                                            setBackgroundType(value);
                                            if (value === 'color') {
                                                onChangeStyle({
                                                    'background-type': value,
                                                    'background-image': '',
                                                    'background-repeat': '',
                                                    'background-size': '',
                                                    'background-position': '',
                                                });
                                            } else {
                                                onChangeStyle({ 'background-type': value, 'background-color': '' });
                                            }
                                        }
                                    }}
                                >
                                    <ToggleButton value="color">Color</ToggleButton>
                                    <ToggleButton value="url">URL</ToggleButton>
                                    <ToggleButton value="upload">Upload Image</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    )}

                    {backgroundType === 'color' && isSectionFieldVisible('background', 'color') && (
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Color
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ColorField
                                    propertyName={'background-color'}
                                    onChangeStyle={onChangeStyle}
                                    formState={formState}
                                    uid={uid}
                                />
                            </Box>
                        </Box>
                    )}

                    {backgroundType === 'url' && (
                        <>
                            {isSectionFieldVisible('background', 'url') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        URL
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>{getTextField('background-image', '')}</Box>
                                </Box>
                            )}
                            {isSectionFieldVisible('background', 'size') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Type
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <ReactSelect
                                            className={'design-variant--secondary'}
                                            data={[
                                                { label: 'Fill', value: 'cover' },
                                                { label: 'Fit', value: 'auto' },
                                            ]}
                                            value={[
                                                getAppBuilderCurrentStyleValue(formState, 'background-size') === 'cover'
                                                    ? 'cover'
                                                    : 'auto',
                                            ]}
                                            update={(value: string) => {
                                                onChangeStyle({
                                                    'background-size': value === 'cover' ? 'cover' : '',
                                                    'background-position': value === 'cover' ? 'center' : '',
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}

                    {backgroundType === 'upload' && (
                        <>
                            {isSectionFieldVisible('background', 'url') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Image
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <BackgroundImageUpload
                                            currentImage={getAppBuilderCurrentStyleValue(formState, 'background-image')}
                                            onImageChange={(dataUrl) => {
                                                onChangeStyle({
                                                    'background-image': dataUrl ? `url(${dataUrl})` : '',
                                                    'background-repeat': 'no-repeat',
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                            {isSectionFieldVisible('background', 'size') && (
                                <Box sx={styles.propertyContainer}>
                                    <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                        Type
                                    </FormLabel>
                                    <Box sx={styles.propertyControlHolder}>
                                        <ReactSelect
                                            className={'design-variant--secondary'}
                                            data={[
                                                { label: 'Fill', value: 'cover' },
                                                { label: 'Fit', value: 'auto' },
                                            ]}
                                            value={[
                                                getAppBuilderCurrentStyleValue(formState, 'background-size') === 'cover'
                                                    ? 'cover'
                                                    : 'auto',
                                            ]}
                                            update={(value: string) => {
                                                onChangeStyle({
                                                    'background-size': value === 'cover' ? 'cover' : '',
                                                    'background-position': value === 'cover' ? 'center' : '',
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </StyleSection>
            )}

            {isSectionVisible('text') && (
                <StyleSection
                    alias={'text'}
                    title={'Text'}
                    collapsedSection={collapsedSection}
                    toggleCollapseState={toggleCollapseState}
                >
                    {isSectionFieldVisible('text', 'font') && (
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Font
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <FontField
                                    propertyName={'font-family'}
                                    onChangeStyle={onChangeStyle}
                                    formState={formState}
                                />
                            </Box>
                        </Box>
                    )}
                    {isSectionFieldVisible('text', 'font-size') && (
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Font Size
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>{getTextField('font-size')}</Box>
                        </Box>
                    )}
                    {isSectionFieldVisible('text', 'color') && (
                        <Box sx={styles.propertyContainer}>
                            <FormLabel sx={styles.propertyTitle} className={'design-variant--secondary'}>
                                Color
                            </FormLabel>
                            <Box sx={styles.propertyControlHolder}>
                                <ColorField
                                    propertyName={'color'}
                                    onChangeStyle={onChangeStyle}
                                    formState={formState}
                                    uid={uid}
                                />
                            </Box>
                        </Box>
                    )}
                </StyleSection>
            )}

            {/*
            <StyleSection
                alias={'spacing'}
                title={'Spacing'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
            <Box>Section content</Box>
            </StyleSection>
            */}
        </Stack>
    );
}
